// src/components/ui/Modal.js
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../../styles/layout.css";

/**
 * Shared Modal (Top-Layer <dialog> version with UA chrome reset)
 * Props:
 *  - open: boolean
 *  - title?: string
 *  - onClose?: () => void
 *  - footer?: ReactNode
 *  - children: ReactNode
 *  - maxWidth?: number (px) — default 560
 *
 * Behavior:
 *  - Uses <dialog>.showModal() (browser Top Layer) so it can’t sit under footers/headers.
 *  - Resets default dialog border/padding/background so there’s no extra box/stroke.
 *  - Backdrop click closes; Esc closes; body scroll is locked while open.
 *  - Fallback to a high z-index portal on older browsers.
 */
export default function Modal({
  open = false,
  title,
  onClose,
  footer,
  children,
  maxWidth = 560,
}) {
  const dialogRef = useRef(null);
  const cardRef = useRef(null);

  // ✅ Keep latest onClose without making the dialog open/close effect re-run each render
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Prevent the browser from auto-focusing the first input (which triggers the
  // mobile keyboard) by focusing the dialog/card container itself.
  useEffect(() => {
    if (!open) return;
    const focusTarget = dialogRef.current || cardRef.current;
    if (!focusTarget || typeof focusTarget.focus !== "function") return;

    // If the user is already interacting with a field, don't steal focus back.
    const active = document.activeElement;
    if (active && focusTarget.contains(active)) return;

    const frame = requestAnimationFrame(() => {
      const current = document.activeElement;
      if (current && current !== document.body && typeof current.blur === "function") {
        current.blur();
      }
      focusTarget.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  // Inject a single stylesheet for all modals
  useEffect(() => {
    const id = "lw-dialog-style";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.textContent = `
        :root{
          --modal-border:#ececf5;
          --modal-ink:#2a0516;
          --modal-muted:#9aa3b2;
          --modal-focus:#2a0516;
          --modal-focus-ring: rgba(42,5,22,.10);
          --modal-shadow: 0 10px 30px rgba(0,0,0,0.20);
          --modal-radius:12px;
          --modal-pad:16px;
          --modal-field-radius:12px;
          --modal-field-bg:#fff;
        }

        /* ====== Top-layer dialog reset ====== */
        dialog.lw-modal {
          border: none;
          padding: 0;
          margin: 0;
          background: transparent;
          width: 100vw;
          height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          max-width: none;
          max-height: none;
          outline: none;
        }
        dialog.lw-modal::backdrop {
          background: rgba(0,0,0,0.35);
        }

        /* ====== Card + body (applies to both render modes) ====== */
        .modal-card{
          background:#fff;
          border-radius:var(--modal-radius);
          box-shadow: var(--modal-shadow);
          overflow:hidden;
          display:flex;
          flex-direction:column;
        }
        .modal-body{
          padding:var(--modal-pad);
          overflow:auto;
        }

        /* ====== Universal form polishing (inside modal only) ====== */
        /* Visually hide labels (keep them accessible for screen readers) */
        .lw-modal .modal-body label,
        [data-lw-modal-version="fallback"] .modal-body label{
          position:absolute!important;
          height:1px!important;
          width:1px!important;
          overflow:hidden!important;
          clip:rect(1px, 1px, 1px, 1px)!important;
          white-space:nowrap!important;
          padding:0!important;
          margin:-1px!important;
          border:0!important;
        }

        /* Inputs, textareas, selects get consistent look */
        .lw-modal .modal-body input[type="text"],
        .lw-modal .modal-body input[type="email"],
        .lw-modal .modal-body input[type="tel"],
        .lw-modal .modal-body input[type="password"],
        .lw-modal .modal-body input[type="url"],
        .lw-modal .modal-body input[type="number"],
        .lw-modal .modal-body textarea,
        .lw-modal .modal-body select,
        [data-lw-modal-version="fallback"] .modal-body input[type="text"],
        [data-lw-modal-version="fallback"] .modal-body input[type="email"],
        [data-lw-modal-version="fallback"] .modal-body input[type="tel"],
        [data-lw-modal-version="fallback"] .modal-body input[type="password"],
        [data-lw-modal-version="fallback"] .modal-body input[type="url"],
        [data-lw-modal-version="fallback"] .modal-body input[type="number"],
        [data-lw-modal-version="fallback"] .modal-body textarea,
        [data-lw-modal-version="fallback"] .modal-body select{
          display:block;
          width:100%;
          background: var(--modal-field-bg);
          border:1px solid var(--modal-border);
          border-radius: var(--modal-field-radius);
          padding:12px 14px;
          font-size:14px;
          line-height:1.4;
          color: var(--modal-ink);
          outline:none;
          transition: border-color .15s ease, box-shadow .15s ease, background-color .15s ease, transform .02s ease;
          -webkit-tap-highlight-color: transparent;
        }

        .lw-modal .modal-body input::placeholder,
        .lw-modal .modal-body textarea::placeholder,
        [data-lw-modal-version="fallback"] .modal-body input::placeholder,
        [data-lw-modal-version="fallback"] .modal-body textarea::placeholder{
          color: var(--modal-muted);
        }

        .lw-modal .modal-body input:focus,
        .lw-modal .modal-body textarea:focus,
        .lw-modal .modal-body select:focus,
        [data-lw-modal-version="fallback"] .modal-body input:focus,
        [data-lw-modal-version="fallback"] .modal-body textarea:focus,
        [data-lw-modal-version="fallback"] .modal-body select:focus{
          border-color: var(--modal-focus);
          box-shadow: 0 0 0 4px var(--modal-focus-ring);
        }

        /* ====== Ultra-modern select (custom arrow, no native chrome) ====== */
        .lw-modal .modal-body select,
        [data-lw-modal-version="fallback"] .modal-body select{
          -webkit-appearance: none;
          appearance: none;
          background-color: var(--modal-field-bg);
          /* inline SVG arrow */
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="%232a0516" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>');
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 18px 18px;
          padding-right: 44px;  /* room for arrow */
          height: 44px;
          cursor: pointer;
        }

        .lw-modal .modal-body select:hover,
        [data-lw-modal-version="fallback"] .modal-body select:hover{
          border-color:#d8d8ee;
          box-shadow: inset 0 0 0 1px rgba(0,0,0,0.02);
        }

        /* Reduce gap between stacked fields */
        .lw-modal .modal-body .field,
        .lw-modal .modal-body .form-row,
        [data-lw-modal-version="fallback"] .modal-body .field,
        [data-lw-modal-version="fallback"] .modal-body .form-row{
          margin-bottom:10px;
        }

        /* Buttons in modals (kept subtle; you likely have your own .btn) */
        .lw-modal .form-actions .btn,
        [data-lw-modal-version="fallback"] .form-actions .btn{
          border-radius:12px;
        }
      `;
      document.head.appendChild(s);
    }
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ✅ Open/close in Top Layer + Esc to close (DO NOT depend on onClose)
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;

    const supportsDialog = typeof dlg.showModal === "function";
    if (!open || !supportsDialog) return;

    const onCancel = (e) => { e.preventDefault(); onCloseRef.current?.(); };
    const onKey = (e) => { if (e.key === "Escape") onCloseRef.current?.(); };

    try { dlg.showModal(); } catch { /* already open */ }
    dlg.addEventListener("cancel", onCancel);
    document.addEventListener("keydown", onKey);

    return () => {
      try { dlg.close(); } catch {}
      dlg.removeEventListener("cancel", onCancel);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!open) return null;

  // Backdrop click: close only if click is outside the card
  const handleDialogClick = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const r = card.getBoundingClientRect();
    const outside =
      e.clientX < r.left || e.clientX > r.right ||
      e.clientY < r.top  || e.clientY > r.bottom;
    if (outside) onCloseRef.current?.();
  };

  const supportsTopLayer = typeof HTMLDialogElement !== "undefined";

  if (supportsTopLayer) {
    return createPortal(
      <dialog
        className="lw-modal"
        ref={dialogRef}
        onMouseDown={handleDialogClick}
        data-lw-modal-version="top-layer"
        tabIndex={-1}
      >
        <div
          className="modal-card"
          ref={cardRef}
          role="document"
          aria-label={title || "Dialog"}
          style={{
            width: `min(92vw, ${maxWidth}px)`,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {title ? <h3 style={{ margin: "12px 16px 0 16px" }}>{title}</h3> : null}
          <div className="modal-body">
            {children}
          </div>
          {footer ? (
            <div className="form-actions" style={{ padding: 16 }}>{footer}</div>
          ) : null}
        </div>
      </dialog>,
      document.body
    );
  }

  // Fallback (older browsers): body portal with high z-index
  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCloseRef.current?.(); }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      data-lw-modal-version="fallback"
    >
      <div
        className="modal-bg"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2147483647,
        }}
      >
        <div
          className="modal-card"
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          aria-label={title || "Dialog"}
          onMouseDown={(e) => e.stopPropagation()}
          tabIndex={-1}
          style={{
            width: `min(92vw, ${maxWidth}px)`,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {title ? <h3 style={{ margin: "12px 16px 0 16px" }}>{title}</h3> : null}
          <div className="modal-body">
            {children}
          </div>
          {footer ? (
            <div className="form-actions" style={{ padding: 16 }}>{footer}</div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
